// The hall's patio-door window: the max-level garden, with the farm's real crops in its beds.
//
// The painted garden is retired as a place you can walk, but its art is the nicest thing in the
// game and the window is where it lives on — you look out of the hall and see the time of day and
// whatever you have planted. Pure geometry and mapping only: no Phaser, no config.js, so
// `node --test` covers it. The Phaser side is ui/GardenWindow.js.

// The twenty beds of the level-3 garden, as [x0,y0, x1,y1, x2,y2, x3,y3] quads in the 1280x720
// art's own coordinates. Lifted unchanged from the retired gardenLevels.js, where they were
// authored in the in-garden edit mode against this exact background.
export const GARDEN_BEDS = [
  [322, 361, 422, 361, 422, 411, 322, 411],
  [461, 361, 561, 361, 561, 411, 461, 411],
  [598, 360, 698, 360, 698, 410, 598, 410],
  [730, 361, 830, 361, 830, 411, 730, 411],
  [863, 361, 963, 361, 963, 411, 863, 411],
  [305, 450, 405, 450, 405, 500, 305, 500],
  [451, 450, 551, 450, 551, 500, 451, 500],
  [595, 451, 695, 451, 695, 501, 595, 501],
  [738, 451, 838, 451, 838, 501, 738, 501],
  [881, 450, 981, 450, 981, 500, 881, 500],
  [282, 546, 382, 546, 382, 596, 282, 596],
  [438, 546, 538, 546, 538, 596, 438, 596],
  [593, 546, 693, 546, 693, 596, 593, 596],
  [745, 546, 845, 546, 845, 596, 745, 596],
  [900, 546, 1000, 546, 1000, 596, 900, 596],
  [257, 636, 367, 636, 367, 696, 257, 696],
  [422, 637, 532, 637, 532, 697, 422, 697],
  [584, 637, 694, 637, 694, 697, 584, 697],
  [748, 636, 858, 636, 858, 696, 748, 696],
  [914, 638, 1024, 638, 1024, 698, 914, 698],
];

// The garden art is 1280x720 — the size of the scene it used to be.
export const GARDEN_W = 1280;
export const GARDEN_H = 720;

// Which point of the garden sits at the glass's top-left corner, and how small it is drawn.
// Tuned by eye against the level-3 art: a slice of beds with a little fence and sky above.
export const WINDOW_SCALE = 0.30;
export const WINDOW_VIEW = { sx: 285, sy: 50 };

export const GARDEN_BG_BY_PHASE = {
  night:   'bg_garden_lvl3_night',
  morning: 'bg_garden_lvl3_morning',
  day:     'bg_garden_lvl3_day',
  evening: 'bg_garden_lvl3_evening',
};

// The light the crops are standing in. The background art is painted per phase, so a crop drawn
// at full brightness on the night garden looks lit by a spotlight; these tints put it under the
// same sky. Day is untinted.
export const CROP_TINT_BY_PHASE = {
  day:     0xffffff,
  morning: 0xffeedd,
  evening: 0xffd8b0,
  night:   0x8ea4c8,
};

export function cropTintForPhase(phase) {
  return CROP_TINT_BY_PHASE[phase] ?? CROP_TINT_BY_PHASE.day;
}

export function gardenBgKey(phase) {
  return GARDEN_BG_BY_PHASE[phase] ?? GARDEN_BG_BY_PHASE.day;
}

// A bed's box: where a plant standing in it should be drawn (centre x, front edge y, width).
export function bedBox(points) {
  const xs = [points[0], points[2], points[4], points[6]];
  const ys = [points[1], points[3], points[5], points[7]];
  const x0 = Math.min(...xs), x1 = Math.max(...xs);
  const y0 = Math.min(...ys), y1 = Math.max(...ys);
  return { cx: (x0 + x1) / 2, front: y1, w: x1 - x0, h: y1 - y0 };
}

// Container transform so garden point (view.sx, view.sy) sits at the glass rect's top-left.
export function windowTransform(glassRect, view, scale) {
  return { x: glassRect.x - view.sx * scale, y: glassRect.y - view.sy * scale, scale };
}

// Never show the scene background through the glass: the garden must cover the pane, so the
// scale is at least pane/garden on both axes, and the view origin is clamped so the garden's
// edges never cross the pane's.
export function fitWindowView(rect, view, minScale, worldW = GARDEN_W, worldH = GARDEN_H) {
  const scale = Math.max(minScale, rect.h / worldH, rect.w / worldW);
  const sx = Math.min(Math.max(0, view.sx), worldW - rect.w / scale);
  const sy = Math.min(Math.max(0, view.sy), worldH - rect.h / scale);
  return { sx, sy, scale };
}

// Which crop goes in which bed. The farm has up to 576 cells and the garden has twenty beds, so
// the window shows the first twenty planted cells, read in a stable order (row by row, the way
// you would walk the farm) — plant something and it appears in the next free bed rather than
// shuffling everything that was already there.
export function bedPlantings(farm, bedCount = GARDEN_BEDS.length) {
  const cells = farm?.cells ?? {};
  const planted = Object.entries(cells)
    .filter(([, p]) => p && p.seed)
    .map(([key, p]) => {
      const [x, y] = key.split(',').map(Number);
      return { x, y, plot: p };
    })
    .sort((a, b) => (a.y - b.y) || (a.x - b.x));
  return planted.slice(0, bedCount);
}
